import type { AuthorizeNetFrontendService } from '@supply-mover-2-0/core';
import {
	PaymentGatewayError,
	PaymentMethodTypeEnum,
	detectCreditCardSubtype,
	type PaymentMethod,
	type PaymentMethodFormData,
	type SupportedPaymentMethodType,
} from '@supply-mover-2-0/core';
import React, { useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../providers/ThemeProvider';
import { useTranslation } from '../providers/TranslationProvider';
import { cn } from '../utils/cn';
import { Text } from './base/Text';
import { Button } from './forms/Button';
import { Input } from './forms/Input';
import { Select } from './forms/Select';

export type {
	PaymentMethod,
	PaymentMethodFormData,
	SupportedPaymentMethodType,
};

interface PaymentMethodFormProps {
	// Mode configuration
	mode: 'create' | 'edit' | 'inline';

	// Data
	clientId?: number;
	supportedPaymentMethods?: Array<SupportedPaymentMethodType>;
	editPaymentMethod?: PaymentMethod;

	// Event handlers
	onSubmit: (data: PaymentMethodFormData) => Promise<void>;
	onCancel?: () => void;

	// Display configuration
	showTypeSelector?: boolean;
	allowedPaymentTypes?: Array<PaymentMethodTypeEnum>;
	preSelectedPaymentType?: PaymentMethodTypeEnum;
	isEditMode?: boolean; // Explicit edit mode flag
	className?: string;
	// Class override for the submit button so branded hosts (the tenant
	// storefront) can restyle it to their accent color — the default
	// `variant="primary"` is the CRM blue. Must include dark:-scoped bg
	// classes to fully displace the variant's dark-mode styles
	// (tailwind-merge keeps a dark:bg-* that has no incoming counterpart).
	submitButtonClassName?: string;

	// Payment processor configuration
	processorType?: string;
	// Callback when secure card field containers are mounted (for CyberSource / NMI hosted fields)
	onSecureFieldsReady?: (
		cardNumberContainerId: string,
		securityCodeContainerId: string,
	) => void;
	// Callback when secure expiration field container is mounted (NMI needs 3 card fields)
	onSecureExpirationFieldReady?: (expirationContainerId: string) => void;
	// Callback when secure ACH field containers are mounted (for CyberSource / NMI hosted fields)
	onSecureACHFieldsReady?: (
		routingNumberContainerId: string,
		accountNumberContainerId: string,
	) => void;
	// Callback when payment type changes (for cleanup during type switch)
	onPaymentTypeChange?: (paymentType: PaymentMethodTypeEnum) => void;
	// Default email for billing (typically from client record)
	defaultEmail?: string;
}

/**
 * Formats a card number with spaces every 4 digits
 * e.g., "4111111111111111" -> "4111 1111 1111 1111"
 */
const formatCardNumber = (value: string): string => {
	// Remove all non-digit characters
	const digits = value.replace(/\D/g, '');

	// Limit to 16 digits
	const limited = digits.slice(0, 16);

	// Add space every 4 digits
	const formatted = limited.replace(/(\d{4})(?=\d)/g, '$1 ');

	return formatted;
};

/**
 * Formats an expiration date with slash between month and year
 * e.g., "1225" -> "12/25", "122025" -> "12/2025"
 */
const formatExpirationDate = (value: string): string => {
	// Remove all non-digit characters
	const digits = value.replace(/\D/g, '');

	// Limit to 6 digits (MM/YYYY)
	const limited = digits.slice(0, 6);

	if (limited.length === 0) {
		return '';
	}

	if (limited.length <= 2) {
		return limited;
	}

	// Add slash after month
	return `${limited.slice(0, 2)}/${limited.slice(2)}`;
};

const getInitialFormData = (
	preSelectedType?: PaymentMethodTypeEnum,
	defaultEmail?: string,
): PaymentMethodFormData => ({
	paymentType: preSelectedType || PaymentMethodTypeEnum.CREDIT_CARD,
	// Credit card fields
	cardNumber: '',
	expirationDate: '',
	nameOnCard: '',
	securityCode: '',
	// eCheck/ACH fields
	routingNumber: '',
	accountNumber: '',
	nameOnAccount: '',
	accountType: 'checking',
	// Address fields
	addressLine1: '',
	addressLine2: '',
	zipCode: '',
	state: '',
	city: '',
	// Email
	email: defaultEmail ?? '',
});

// Container IDs for CyberSource Flex Microform
const CYBERSOURCE_CARD_NUMBER_CONTAINER = 'cybersource-card-number';
const CYBERSOURCE_SECURITY_CODE_CONTAINER = 'cybersource-security-code';
const CYBERSOURCE_ROUTING_NUMBER_CONTAINER = 'cybersource-routing-number';
const CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER = 'cybersource-account-number';

// Container IDs for NMI Collect.JS hosted fields
const NMI_CARD_NUMBER_CONTAINER = 'nmi-card-number';
const NMI_EXPIRATION_CONTAINER = 'nmi-expiration';
const NMI_CVV_CONTAINER = 'nmi-cvv';
const NMI_ROUTING_NUMBER_CONTAINER = 'nmi-routing-number';
const NMI_ACCOUNT_NUMBER_CONTAINER = 'nmi-account-number';

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
	mode,
	supportedPaymentMethods = [],
	editPaymentMethod,
	onSubmit,
	onCancel,
	showTypeSelector = true,
	allowedPaymentTypes = [
		PaymentMethodTypeEnum.CREDIT_CARD,
		PaymentMethodTypeEnum.ACH,
	],
	preSelectedPaymentType,
	isEditMode = false,
	className,
	submitButtonClassName,
	processorType,
	onSecureFieldsReady,
	onSecureExpirationFieldReady,
	onSecureACHFieldsReady,
	onPaymentTypeChange,
	defaultEmail,
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();

	const [formData, setFormData] = useState<PaymentMethodFormData>(() =>
		getInitialFormData(preSelectedPaymentType, defaultEmail),
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const authNetServiceRef = useRef<AuthorizeNetFrontendService | null>(null);

	// Initialize form data for edit mode or pre-selected payment type
	useEffect(() => {
		if (isEditMode && editPaymentMethod) {
			// Format expiration date for display
			let formattedExpiration = '';

			if (editPaymentMethod.paymentExpirationDate) {
				const expDate = new Date(editPaymentMethod.paymentExpirationDate);
				const month = (expDate.getMonth() + 1).toString().padStart(2, '0');
				const year = expDate.getFullYear().toString();
				formattedExpiration = `${month}/${year}`;
			}

			setFormData({
				paymentType: editPaymentMethod.paymentType,
				// Credit card fields - show masked/display values
				cardNumber: editPaymentMethod.paymentLastFour
					? `****${editPaymentMethod.paymentLastFour}`
					: '',
				expirationDate: formattedExpiration,
				nameOnCard: editPaymentMethod.paymentName || '',
				securityCode: '', // Never show security code
				// eCheck/ACH fields - show display values
				routingNumber: editPaymentMethod.paymentRoutingNumber || '',
				accountNumber: editPaymentMethod.paymentLastFour
					? `****${editPaymentMethod.paymentLastFour}`
					: '',
				nameOnAccount: editPaymentMethod.paymentName || '',
				accountType: editPaymentMethod.accountType || 'checking',
				// Address fields
				addressLine1: editPaymentMethod.paymentAddress1 || '',
				addressLine2: editPaymentMethod.paymentAddress2 || '',
				zipCode: editPaymentMethod.paymentZip || '',
				state: editPaymentMethod.paymentState || '',
				city: editPaymentMethod.paymentCity || '',
				// Email
				email: defaultEmail ?? '',
			});
		} else if (
			mode === 'create' &&
			preSelectedPaymentType &&
			formData.paymentType !== preSelectedPaymentType
		) {
			// Set pre-selected payment type for create mode if it's different
			setFormData((prev) => ({
				...prev,
				paymentType: preSelectedPaymentType,
			}));
		} else if (
			(mode === 'create' || mode === 'inline') &&
			preSelectedPaymentType &&
			formData.paymentType !== preSelectedPaymentType
		) {
			// Handle inline mode as well
			setFormData((prev) => ({
				...prev,
				paymentType: preSelectedPaymentType,
			}));
		}
	}, [
		mode,
		editPaymentMethod,
		preSelectedPaymentType,
		formData.paymentType,
		defaultEmail,
		isEditMode,
	]);

	// Update email when defaultEmail changes (e.g., when client data loads)
	useEffect(() => {
		if (defaultEmail != null && formData.email === '') {
			setFormData((prev) => ({
				...prev,
				email: defaultEmail,
			}));
		}
	}, [defaultEmail, formData.email]);

	// Cleanup on unmount
	useEffect(
		() => () => {
			if (authNetServiceRef.current) {
				authNetServiceRef.current.cleanup();
			}
		},
		[],
	);

	// Notify parent when CyberSource secure card field containers are ready
	useEffect(() => {
		if (
			processorType === 'cybersource' &&
			formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
			!isEditMode &&
			onSecureFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const cardContainer = document.getElementById(
					CYBERSOURCE_CARD_NUMBER_CONTAINER,
				);
				const cvvContainer = document.getElementById(
					CYBERSOURCE_SECURITY_CODE_CONTAINER,
				);

				if (cardContainer != null && cvvContainer != null) {
					onSecureFieldsReady(
						CYBERSOURCE_CARD_NUMBER_CONTAINER,
						CYBERSOURCE_SECURITY_CODE_CONTAINER,
					);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [processorType, formData.paymentType, isEditMode, onSecureFieldsReady]);

	// Notify parent when NMI secure card field containers are ready
	useEffect(() => {
		if (
			processorType === 'nmi' &&
			formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
			!isEditMode &&
			onSecureFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const cardContainer = document.getElementById(
					NMI_CARD_NUMBER_CONTAINER,
				);
				const cvvContainer = document.getElementById(NMI_CVV_CONTAINER);
				const expContainer = document.getElementById(NMI_EXPIRATION_CONTAINER);

				if (cardContainer != null && cvvContainer != null) {
					onSecureFieldsReady(NMI_CARD_NUMBER_CONTAINER, NMI_CVV_CONTAINER);
				}

				// NMI also needs the expiration field
				if (expContainer != null && onSecureExpirationFieldReady != null) {
					onSecureExpirationFieldReady(NMI_EXPIRATION_CONTAINER);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [
		processorType,
		formData.paymentType,
		isEditMode,
		onSecureFieldsReady,
		onSecureExpirationFieldReady,
	]);

	// Notify parent when CyberSource secure ACH field containers are ready
	useEffect(() => {
		if (
			processorType === 'cybersource' &&
			formData.paymentType === PaymentMethodTypeEnum.ACH &&
			!isEditMode &&
			onSecureACHFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const routingContainer = document.getElementById(
					CYBERSOURCE_ROUTING_NUMBER_CONTAINER,
				);
				const accountContainer = document.getElementById(
					CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER,
				);

				if (routingContainer != null && accountContainer != null) {
					onSecureACHFieldsReady(
						CYBERSOURCE_ROUTING_NUMBER_CONTAINER,
						CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER,
					);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [processorType, formData.paymentType, isEditMode, onSecureACHFieldsReady]);

	// Notify parent when NMI secure ACH field containers are ready
	useEffect(() => {
		if (
			processorType === 'nmi' &&
			formData.paymentType === PaymentMethodTypeEnum.ACH &&
			!isEditMode &&
			onSecureACHFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const routingContainer = document.getElementById(
					NMI_ROUTING_NUMBER_CONTAINER,
				);
				const accountContainer = document.getElementById(
					NMI_ACCOUNT_NUMBER_CONTAINER,
				);

				if (routingContainer != null && accountContainer != null) {
					onSecureACHFieldsReady(
						NMI_ROUTING_NUMBER_CONTAINER,
						NMI_ACCOUNT_NUMBER_CONTAINER,
					);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [processorType, formData.paymentType, isEditMode, onSecureACHFieldsReady]);

	const handleInputChange = (
		field: keyof PaymentMethodFormData,
		value: string,
	) => {
		setFormData((prev) => {
			const updatedData = {
				...prev,
				[field]: value,
			};

			// If card number is being updated, detect the credit card subtype
			if (field === 'cardNumber' && value.length >= 6) {
				const detectedSubtype = detectCreditCardSubtype(value);
				if (detectedSubtype) {
					// Note: We don't set the subtype in form data since it's not part of the form
					// but we'll pass it to the API when creating the payment method
				}
			}

			return updatedData;
		});
	};

	const handlePaymentTypeChange = (value: string) => {
		const paymentType = value as PaymentMethodTypeEnum;

		// Notify parent about payment type change (for cleanup during type switch)
		onPaymentTypeChange?.(paymentType);

		setFormData((prev) => ({
			...prev,
			paymentType,
			// Clear fields when switching types
			cardNumber: '',
			expirationDate: '',
			nameOnCard: '',
			securityCode: '',
			routingNumber: '',
			accountNumber: '',
			nameOnAccount: '',
			accountType: 'checking',
		}));
		setError(null);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		try {
			setLoading(true);
			await onSubmit(formData);

			// Reset form after successful submission (for create mode)
			if (!isEditMode) {
				setFormData(getInitialFormData(preSelectedPaymentType, defaultEmail));
			}
		} catch (error) {
			let errorMessage = t(
				'Failed to process payment method. Please try again.',
			);

			if (error instanceof PaymentGatewayError) {
				switch (error.code) {
					case 'VALIDATION_ERROR':
						errorMessage = error.message;
						break;
					case 'E_WC_05':
						errorMessage = t(
							'Invalid credit card number. Please check and try again.',
						);
						break;
					case 'E_WC_06':
						errorMessage = t(
							'Invalid expiration date. Please check and try again.',
						);
						break;
					case 'E_WC_07':
						errorMessage = t(
							'Invalid security code. Please check and try again.',
						);
						break;
					case 'E_WC_15':
						errorMessage = t('Credit card number and card type do not match.');
						break;
					default:
						errorMessage = t(`Payment error: ${error.message}`);
						break;
				}
			} else if (error instanceof Error) {
				errorMessage = error.message;
			}

			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// Check if using hosted iframe fields for secure card/ACH data entry
	const useSecureHostedFields =
		(processorType === 'cybersource' || processorType === 'nmi') && !isEditMode;

	// Shared container classes for all hosted iframe fields (card + ACH).
	// NMI: No padding — padding is set inside the iframe's input via customCss
	// so the iframe fills the entire container and any click within the border
	// triggers focus (important for mobile tap targets).
	// CyberSource: Keep standard padding — Flex Microform handles its own sizing.
	const secureFieldClasses = cn(
		'block w-full text-sm h-[38px] overflow-hidden',
		processorType !== 'nmi' && 'px-3 py-2',
		colors.classes.formFieldBase,
		colors.classes.formFieldDefault,
		colors.classes.formFieldFocus,
	);

	const renderCreditCardFields = () => {
		const loadingPlaceholder = (width: string) => (
			<div className="h-full flex items-center">
				<div
					className={`animate-pulse bg-gray-200 dark:bg-gray-700 h-4 ${width} rounded`}
				/>
			</div>
		);

		return (
			<div className="space-y-4">
				{/* Card Number Field */}
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Card Number')}
						{!isEditMode && <span className="text-red-500 ml-1">*</span>}
						{isEditMode && (
							<span className={`ml-2 text-xs ${colors.classes.textSecondary}`}>
								({t('Display only')})
							</span>
						)}
					</label>
					{useSecureHostedFields ? (
						<div
							id={
								processorType === 'nmi'
									? NMI_CARD_NUMBER_CONTAINER
									: CYBERSOURCE_CARD_NUMBER_CONTAINER
							}
							className={secureFieldClasses}
						>
							{loadingPlaceholder('w-32')}
						</div>
					) : (
						<Input
							type="text"
							value={formData.cardNumber}
							onChange={(e) => {
								const formatted = formatCardNumber(e.target.value);
								handleInputChange('cardNumber', formatted);
							}}
							placeholder={t('1234 5678 9012 3456')}
							required={!isEditMode}
							disabled={loading || isEditMode}
							maxLength={19}
						/>
					)}
				</div>

				{/* Name on Card Field */}
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Name on Card')}
						<span className="text-red-500 ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.nameOnCard}
						onChange={(e) => {
							handleInputChange('nameOnCard', e.target.value);
						}}
						placeholder={t('Full name as shown on card')}
						required
						disabled={loading}
					/>
				</div>

				{/* Expiration Date and Security Code Row */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Expiration Date')}
							<span className="text-red-500 ml-1">*</span>
						</label>
						{/* NMI uses a hosted field for expiration; CyberSource uses a regular input */}
						{processorType === 'nmi' && !isEditMode ? (
							<div id={NMI_EXPIRATION_CONTAINER} className={secureFieldClasses}>
								{loadingPlaceholder('w-16')}
							</div>
						) : (
							<Input
								type="text"
								value={formData.expirationDate}
								onChange={(e) => {
									const formatted = formatExpirationDate(e.target.value);
									handleInputChange('expirationDate', formatted);
								}}
								placeholder={t('MM/YY')}
								required
								disabled={loading}
								maxLength={7}
							/>
						)}
					</div>

					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Security Code')}
							{!isEditMode && <span className="text-red-500 ml-1">*</span>}
							{isEditMode && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Not required for updates')})
								</span>
							)}
						</label>
						{useSecureHostedFields ? (
							<div
								id={
									processorType === 'nmi'
										? NMI_CVV_CONTAINER
										: CYBERSOURCE_SECURITY_CODE_CONTAINER
								}
								className={secureFieldClasses}
							>
								{loadingPlaceholder('w-12')}
							</div>
						) : (
							<Input
								type="text"
								value={formData.securityCode}
								onChange={(e) => {
									const digits = e.target.value.replace(/\D/g, '');
									handleInputChange('securityCode', digits);
								}}
								placeholder={t('123')}
								required={!isEditMode}
								disabled={loading || isEditMode}
								maxLength={4}
							/>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Check if we should use hosted secure fields for ACH
	const useSecureACHFields = useSecureHostedFields;

	const renderECheckFields = () => {
		// Determine which container IDs to use based on processor
		const routingContainerId =
			processorType === 'nmi'
				? NMI_ROUTING_NUMBER_CONTAINER
				: CYBERSOURCE_ROUTING_NUMBER_CONTAINER;
		const accountContainerId =
			processorType === 'nmi'
				? NMI_ACCOUNT_NUMBER_CONTAINER
				: CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER;

		return (
			<div className="space-y-4">
				{useSecureACHFields && (
					<div
						className={`p-3 rounded-md ${colors.classes.bgSecondary} border ${colors.classes.borderDefault}`}
					>
						<Text className={`text-sm ${colors.classes.textSecondary}`}>
							{t(
								'To verify your bank account, a temporary $0.01 charge will be made and immediately refunded. This may take a few days to appear on your statement.',
							)}
						</Text>
					</div>
				)}
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Account Type')}
					</label>
					<Select
						value={formData.accountType}
						onChange={(value) => {
							handleInputChange('accountType', value);
						}}
						options={[
							{ value: 'checking', label: t('Checking') },
							{ value: 'savings', label: t('Savings') },
						]}
						disabled={loading}
					/>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Routing Number')}
							{!isEditMode && <span className="text-red-500 ml-1">*</span>}
							{isEditMode && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Display only')})
								</span>
							)}
						</label>
						{useSecureACHFields ? (
							<div id={routingContainerId} className={secureFieldClasses} />
						) : (
							<Input
								type="text"
								value={formData.routingNumber}
								onChange={(e) => {
									handleInputChange('routingNumber', e.target.value);
								}}
								placeholder={t('123456789')}
								required={!isEditMode}
								disabled={loading || isEditMode}
							/>
						)}
					</div>

					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Account Number')}
							{!isEditMode && <span className="text-red-500 ml-1">*</span>}
							{isEditMode && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Display only')})
								</span>
							)}
						</label>
						{useSecureACHFields ? (
							<div id={accountContainerId} className={secureFieldClasses} />
						) : (
							<Input
								type="text"
								value={formData.accountNumber}
								onChange={(e) => {
									handleInputChange('accountNumber', e.target.value);
								}}
								placeholder={t('Account number')}
								required={!isEditMode}
								disabled={loading || isEditMode}
							/>
						)}
					</div>
				</div>

				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Name on Account')}
						<span className="text-red-500 ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.nameOnAccount}
						onChange={(e) => {
							handleInputChange('nameOnAccount', e.target.value);
						}}
						placeholder={t('Full name as shown on account')}
						required
						disabled={loading}
					/>
				</div>
			</div>
		);
	};

	const renderBillingAddressFields = () => (
		<div className="space-y-4">
			<div>
				<label
					className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
				>
					{t('Address Line 1')}
					<span className="text-red-500 ml-1">*</span>
				</label>
				<Input
					type="text"
					value={formData.addressLine1}
					onChange={(e) => {
						handleInputChange('addressLine1', e.target.value);
					}}
					placeholder={t('Street address')}
					required
					disabled={loading}
				/>
			</div>

			<div>
				<label
					className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
				>
					{t('Address Line 2')} {t('(Optional)')}
				</label>
				<Input
					type="text"
					value={formData.addressLine2}
					onChange={(e) => {
						handleInputChange('addressLine2', e.target.value);
					}}
					placeholder={t('Apartment, suite, etc.')}
					disabled={loading}
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('City')}
						<span className="text-red-500 ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.city}
						onChange={(e) => {
							handleInputChange('city', e.target.value);
						}}
						placeholder={t('City')}
						required
						disabled={loading}
					/>
				</div>

				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('State')}
						<span className="text-red-500 ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.state}
						onChange={(e) => {
							handleInputChange('state', e.target.value);
						}}
						placeholder={t('State')}
						required
						disabled={loading}
					/>
				</div>

				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('ZIP Code')}
						<span className="text-red-500 ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.zipCode}
						onChange={(e) => {
							handleInputChange('zipCode', e.target.value);
						}}
						placeholder={t('12345')}
						required
						disabled={loading}
					/>
				</div>
			</div>

			{/* Email field - required for CyberSource */}
			{processorType === 'cybersource' && (
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Email')}
						<span className="text-red-500 ml-1">*</span>
					</label>
					<Input
						type="email"
						value={formData.email}
						onChange={(e) => {
							handleInputChange('email', e.target.value);
						}}
						placeholder={t('email@example.com')}
						required
						disabled={loading}
					/>
				</div>
			)}
		</div>
	);

	const paymentTypeOptions = allowedPaymentTypes.map((type) => ({
		value: type,
		label: t(type),
	}));

	return (
		<div className={cn('space-y-6', className)}>
			<form
				id="payment-method-form"
				onSubmit={handleSubmit}
				className="space-y-6"
			>
				{/* Payment Type Selector */}
				{showTypeSelector && paymentTypeOptions.length > 1 && (
					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Payment Type')}
						</label>
						<Select
							value={formData.paymentType}
							onChange={handlePaymentTypeChange}
							options={paymentTypeOptions}
							disabled={loading || preSelectedPaymentType != null}
						/>
					</div>
				)}

				{/* Payment Method Fields */}
				{formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
					renderCreditCardFields()}
				{formData.paymentType === PaymentMethodTypeEnum.ACH &&
					renderECheckFields()}

				{/* Billing Address */}
				<div>
					<Text
						className={`text-lg font-semibold ${colors.classes.textPrimary} mb-4`}
					>
						{t('Billing Address')}
					</Text>
					{renderBillingAddressFields()}
				</div>

				{/* Error Display */}
				{error && (
					<div
						className={`p-4 rounded-md ${colors.classes.bgDanger} border ${colors.classes.borderDanger}`}
					>
						<Text className={colors.classes.textOnDanger}>{error}</Text>
					</div>
				)}

				{/* Form Actions - only show for non-inline modes */}
				{mode !== 'inline' && (
					<div className="flex justify-end gap-3 pt-4">
						{onCancel && (
							<Button
								type="button"
								variant="outline"
								onClick={onCancel}
								disabled={loading}
							>
								{t('Cancel')}
							</Button>
						)}
						<Button
							type="submit"
							variant="primary"
							loading={loading}
							disabled={loading}
							className={submitButtonClassName}
						>
							{loading
								? t('Processing...')
								: mode === 'edit'
									? t('Update Payment Method')
									: t('Add Payment Method')}
						</Button>
					</div>
				)}
			</form>
		</div>
	);
};
